import { JourneyElements } from '../../interface/journey-elements';
import {
  JourneyElement,
  JourneyElementAcceptanceEligibility,
  PostJourneysGetJourneyResponse,
  PostJourneysGetJourneyResponseDataFlightsInner,
  PostJourneysGetJourneyResponseDataTravelersInner,
} from 'src/sdk_checkin';
import { Lounges } from '../../interface/lounges';
import { FirstBaggages } from '../../interface/first-baggages';
import { JuniorPilots } from '../../interface/junior-pilots';
import { isSuffixSegment } from '@common/helpers';

/**
 * チェックイン処理における、一般的なCPR情報操作を実装します。
 *
 * `{instance}.cprResult` によって生のCPR取得結果にアクセスできるほか、
 * 搭乗者別に区分けしたJourneyElementの取得などを実装します。
 */
export class CprResultWrapper {
  /**
   * CPR情報から搭乗者IDをキー、搭乗者ごとのjourneyElement配列を値とするマップを作成する
   * journeyElement配列は、CPR情報のflightsの順序を保持する
   */
  private readonly _journeyElementsByTraveler = new Map<string, JourneyElement[]>();

  constructor(public readonly cprResult: PostJourneysGetJourneyResponse) {
    const journeyElementObj = cprResult.data.journeyElements as JourneyElements;
    cprResult.data.travelers.forEach((traveler) => {
      const journeyElements: JourneyElement[] = cprResult.data.flights.map((flight) => {
        return journeyElementObj[flight.id][traveler.id];
      });
      this._journeyElementsByTraveler.set(traveler.id, journeyElements);
    });
  }

  /**
   * 搭乗者Idを指定し、搭乗者ごとのjourneyElement配列を返す
   * journeyElement配列は、CPR情報のflightsの順序を保持します。
   * @param travelerId 対象の搭乗者.id
   * @returns 指定された搭乗者のjourneyElement配列
   */
  public getJourneyElementsByTraveler(travelerId: string) {
    const journeyElements = this._journeyElementsByTraveler.get(travelerId);
    return journeyElements;
  }

  /**
   * 搭乗者Idを指定しその搭乗者のjourneyElementの中に1便でもチェックイン状態のセグメントが含まれているかを返す
   * @param travelerId
   * @returns チェックイン済みセグメントが存在する場合true、それ以外の場合flase
   */
  public isIncludeCheckedinSegment(travelerId: string) {
    const journeyElements = this._journeyElementsByTraveler.get(travelerId);
    return journeyElements?.some((journeyElement) => CprResultWrapper.isCheckedIn(journeyElement));
  }

  /**
   * 搭乗者Idを指定しその搭乗者のjourneyElementの中に1便でもチェックイン可能であるセグメントが含まれているかを返す
   * @param travelerId
   * @returns チェックイン可能であるセグメントが含まれていればtrue、そうでなければfalse
   */
  public isIncludeCanCheckinSegment(travelerId: string) {
    const journeyElements = this._journeyElementsByTraveler.get(travelerId);
    return journeyElements?.some(
      (journeyElement) =>
        journeyElement.checkInStatus === JourneyElement.CheckInStatusEnum.NotAccepted &&
        journeyElement.acceptanceEligibility.status === JourneyElementAcceptanceEligibility.StatusEnum.Eligible
    );
  }

  /**
   * journeyElement配列を確認し、同行者が存在するかどうかを判定する
   * @returns 全てのjourneyElementのorderIdを確認し、異なるorderIdを持つものが存在すればtrue、そうでなければfalse
   */
  public isIncludeCompanion() {
    const allJourneyElements = [...this._journeyElementsByTraveler.values()].flat();
    const orderIds = allJourneyElements?.map((journeyElement) => journeyElement?.orderId);
    const orderIdSet = new Set(orderIds);
    return orderIdSet.size > 1;
  }

  /**
   * 搭乗者Idを指定して、その搭乗者のacceptanceStatusSummaryを見て情報のチェックインに関わる情報の変更ができる状態かを判定する
   * @param travelerId
   * @returns acceptanceStatusSummaryがacceptedまたはPartialであればtrue、それ以外であればfalse
   */
  public canChangeCheckinInformationByAcceptanceStatus(travelerId: string) {
    const traveler = this.cprResult.data.travelers.filter((traveler) => traveler.id === travelerId)[0];
    return (
      traveler?.acceptanceStatusSummary ===
        PostJourneysGetJourneyResponseDataTravelersInner.AcceptanceStatusSummaryEnum.Accepted ||
      traveler?.acceptanceStatusSummary ===
        PostJourneysGetJourneyResponseDataTravelersInner.AcceptanceStatusSummaryEnum.Partial
    );
  }

  /**
   * 座席変更時間外のフライトかを判定する
   * @returns 全てのフライトについて座席変更時間外であればtrue、そうでなければfalse
   */
  public isNotShowChageSeatButton() {
    return this.cprResult.data.flights.every((flight) => !flight.isSeatChangeEligible);
  }

  /**
   * 搭乗者入力情報変更時間外かを判定する
   * @returns 搭乗者入力情報変更時間外であればtrue、そうでなければfalse
   */
  public isNotShowChangePassengerInputButton() {
    return !this.cprResult.data.isUpdateTravelersEligible;
  }

  /**
   * サービス変更時間外の搭乗者かを判定する
   * @returns サービス変更時間外の搭乗者であればtrue、そうでなければfalse
   */
  public isNotShowChangeServiceButton() {
    return !this.cprResult.data.isServiceApplicationEligible;
  }

  /**
   * 指定された搭乗者についてサービスが申し込まれているかを判定するメソッド
   * @param travelerId サービスを申し込んでいる搭乗者のID
   * @returns サービスが1つでも申し込まれていればtrue、そうでなければfalse
   */
  public isAppliedServices(travelerId: string) {
    // 事前手荷物が申し込まれているかのフラグを作成
    const firstBaggages = this.cprResult.data.services?.baggage?.firstBaggage as FirstBaggages;
    const firstBaggageApplied = !!firstBaggages && !!firstBaggages[travelerId] ? true : false;

    // ラウンジが申し込まれているかのフラグを作成
    const lounges = this.cprResult.data.services?.lounge as Lounges;
    let isLoungeApplied = false;
    const flights = this.cprResult.data.flights;
    if (flights) {
      isLoungeApplied = flights.some((flight) => {
        const loungeByFlights = lounges ? lounges[flight.id] : undefined;
        if (!!loungeByFlights && !!loungeByFlights[travelerId]) {
          return true;
        }
        return false;
      });
    }
    // ジュニアパイロットが申し込まれているかのフラグを作成
    const juniorPilots = this.cprResult.data.services?.juniorPilot as JuniorPilots;
    let isJuniorPilotApplied = false;
    if (flights) {
      isJuniorPilotApplied = flights.some((flight) => {
        const juniorPilotByFlights = juniorPilots ? juniorPilots[flight.id] : undefined;
        if (!!juniorPilotByFlights && !!juniorPilotByFlights[travelerId]) {
          return true;
        }
        return false;
      });
    }

    // サービスが1件でも申し込まれているかどうかのフラグの作成
    return firstBaggageApplied || isLoungeApplied || isJuniorPilotApplied;
  }

  /**
   * 指定されたjourneyElementがチェックイン済みかどうかを判定する
   * @param journeyElement
   * @returns 指定されたjourneyElement.acceptanceStatusがacceptedかstandbyの場合true、それ以外はfalse
   */
  public static isCheckedIn(journeyElement: JourneyElement) {
    return (
      journeyElement.checkInStatus === JourneyElement.CheckInStatusEnum.Accepted ||
      journeyElement.checkInStatus === JourneyElement.CheckInStatusEnum.StandBy
    );
  }

  /**
   * 指定されたセグメント、搭乗者に紐づくjourneyElementからcabinクラスを重複を除いて返す
   * @param flights セグメントの配列、指定しない場合はCPRの全てのセグメント
   * @param traveler 搭乗者の配列、指定しない場合はCPRの全ての搭乗者
   * @returns 重複を除いたcabinクラスの配列
   */
  extractNonDuplicateCabinClassesBySegment(
    flight: PostJourneysGetJourneyResponseDataFlightsInner,
    travelers: PostJourneysGetJourneyResponseDataTravelersInner[] = this.cprResult.data.travelers || []
  ): string[] {
    // journeyElementから選択されたセグメント、選択された搭乗者を元に、キャビンクラスを重複を取り除き抽出
    const cabinSet = new Set<string>();
    const journeyElements = this.cprResult.data.journeyElements as JourneyElements;
    travelers.forEach((passenger) => {
      const cabin = journeyElements[flight.id][passenger.id].cabin;

      if (cabin) {
        cabinSet.add(cabin);
      }
    });

    return Array.from(cabinSet);
  }

  /**
   * 指定されたセグメント、指定された搭乗者にてキャビンクラスが混在しているかを返す
   * キャビンクラス混在とは、指定されたセグメントを繰り返し、1つでもキャビンクラスが混在するようなセグメントが存在すれば混在と返す
   * @param flights 判定対象となるセグメント
   * @param travelers 判定対象となる搭乗者
   * @returns キャビンクラスが混在状態であればtrue、そうでなければfalse
   */
  isCabinClassMixed(
    flights: PostJourneysGetJourneyResponseDataFlightsInner[] = this.cprResult.data.flights || [],
    travelers: PostJourneysGetJourneyResponseDataTravelersInner[] = this.cprResult.data.travelers || []
  ) {
    // セグメントごとに指定された搭乗者について重複なしでキャビンクラスを取り出す
    const cabinsBySegment = flights.map((flight) => {
      return this.extractNonDuplicateCabinClassesBySegment(flight, travelers);
    });

    // 各セグメントのキャビンクラスのうち、1セグメントでも要素数が2以上のものが存在すればキャビンクラス混在
    return cabinsBySegment.some((cabins) => cabins.length >= 2);
  }

  /**
   * CPRに含まれる全ての便がSuffix便であるかを判定する。
   * @returns 全便がSuffix便であればtrue、1便でもSuffix便でないものがあればfalse
   */
  isAllSuffixSegment() {
    return this.cprResult.data.flights.every((flight) => isSuffixSegment(flight.marketingFlightNumber || ''));
  }
}
